import { useEffect, useRef, useState } from 'react';
import { Flex, Icon, chakra } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { Pencil } from 'lucide-react';
import { iconSize } from '@duke-hq/ui/constants/scalable';

/**
 * Inline-editable title for an editor's secondary header. Renames on commit
 * (blur / Enter) via `onRename`; there's no save button, the change autosaves
 * like the rest of the editor.
 *
 * The whole control is a single contentEditable span, so the same element
 * provides both the resting text and the editing field. Because one element's
 * intrinsic width drives the layout in both states, the box never visibly jumps
 * when entering or leaving edit mode. It shows the full title at its natural
 * width; when the bar is too narrow the breadcrumb row it sits in scrolls to
 * reveal it (see EditorBreadcrumbs), so the title is never clipped.
 */
const EditableSpan = chakra('span');

export function EditableResourceTitle({
  title,
  onRename,
  ariaLabel,
}: {
  title: string;
  onRename: (value: string) => void | Promise<void>;
  /**
   * Accessible name for the editable field. Defaults to a generic "Title", but
   * callers should name the thing being edited (e.g. "Conversation title", "Unit
   * title") so the field reads correctly per editor.
   */
  ariaLabel?: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const spanRef = useRef<HTMLSpanElement>(null);
  // The last value we put in the span (initial title or a committed rename).
  // The save round-trip means the `title` prop lags our own commit, so we sync
  // the prop in only when it genuinely differs from what we last showed,
  // otherwise the stale prop would clobber the value the user just typed.
  const shownValue = useRef(title);

  useEffect(() => {
    if (!isEditing && spanRef.current && title !== shownValue.current) {
      spanRef.current.textContent = title;
      shownValue.current = title;
    }
  }, [title, isEditing]);

  const startEditing = () => {
    setIsEditing(true);
  };

  useEffect(() => {
    if (isEditing && spanRef.current) {
      const span = spanRef.current;
      span.focus();
      const range = document.createRange();
      range.selectNodeContents(span);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [isEditing]);

  const commit = async () => {
    setIsEditing(false);
    const next = spanRef.current?.textContent?.trim() ?? '';
    if (next && next !== title) {
      // Optimistically keep the typed value on screen; the prop catches up
      // after the save. If `onRename` rejects, roll back to the title we had
      // when the edit started so the header doesn't strand a failed value.
      const previous = title;
      if (spanRef.current) {
        spanRef.current.textContent = next;
      }
      shownValue.current = next;
      try {
        await onRename(next);
      } catch {
        // Only roll back if nothing has superseded this edit in the meantime
        // (a newer commit, or the user re-opening the field to type again).
        if (shownValue.current === next) {
          if (spanRef.current) {
            spanRef.current.textContent = previous;
          }
          shownValue.current = previous;
        }
      }
    } else if (spanRef.current) {
      // Revert blank (or unchanged) edits back to the current title.
      spanRef.current.textContent = title;
      shownValue.current = title;
    }
  };

  const cancel = () => {
    if (spanRef.current) {
      spanRef.current.textContent = title;
    }
    shownValue.current = title;
    setIsEditing(false);
  };

  return (
    <Flex
      align="center"
      gap="2"
      minW={0}
      px="2"
      h="8"
      borderRadius="md"
      cursor="text"
      bg={isEditing ? 'bg.muted' : undefined}
      _hover={{ bg: 'bg.muted' }}
      onClick={() => {
        if (!isEditing) {
          startEditing();
        }
      }}
    >
      <EditableSpan
        ref={spanRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label={ariaLabel ?? t`Title`}
        spellCheck={false}
        fontSize="sm"
        fontWeight="medium"
        outline="none"
        whiteSpace="nowrap"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            spanRef.current?.blur();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        onBlur={commit}
      >
        {title}
      </EditableSpan>
      <Icon as={Pencil} boxSize={iconSize.xs} color="fg.muted" flexShrink={0} />
    </Flex>
  );
}
